// src/adminFlow/AdminProductsPage.js
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import API_BASE from '../apiBase';
import { ArrowUpDown, Search, Plus, Home, Trash2, Pencil, X } from 'lucide-react';
import useAutoRefresh from './useAutoRefresh';
import { broadcastInvalidation, useListenInvalidation } from './adminInvalidation';

function fmt(n, c = 'CAD') {
  const v = Number(n ?? 0);
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(v); }
  catch { return `$${v.toFixed(2)}`; }
}
const clip = (s, n = 80) => (s?.length > n ? s.slice(0, n - 1) + '…' : (s || ''));

export default function AdminProductsPage() {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('name');
  const [dir, setDir] = useState('asc');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal state
  const [confirmItem, setConfirmItem] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const myId = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: q.trim(), sort, dir });
      const r = await fetch(`${API_BASE}/api/admin/products?${params}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      const j = await r.json().catch(() => ({}));
      if (reqId.current !== myId) return;
      setRows(r.ok ? j.items || [] : []);
    } finally {
      if (reqId.current === myId) setLoading(false);
    }
  }, [q, sort, dir]);

  const first = useRef(true);
  useEffect(() => {
    let t;
    if (first.current) { first.current = false; load(); }
    else { t = setTimeout(load, 300); }
    return () => clearTimeout(t);
  }, [load]);

  // auto refresh + cross-tab invalidations
  useAutoRefresh(load, { intervalMs: 30000, refetchOnFocus: true });
  useListenInvalidation(() => { load(); });

  function toggleSort(col) {
    if (sort === col) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(col); setDir('asc'); }
  }
  function applySearch(e) { e?.preventDefault(); load(); }

  async function confirmDelete() {
    if (!confirmItem) return;
    setDeleting(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/api/admin/products/${confirmItem.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Delete failed');
      setConfirmItem(null);
      await load(); // refresh this tab
      broadcastInvalidation('product.deleted', { id: confirmItem.id }); // notify other tabs
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  const currency = rows[0]?.currency || 'CAD';

  // ≤ 5 is low, <= 0 is out
  const stockBadge = (qty) => {
    const n = Number(qty || 0);
    if (n <= 0) return { label: 'Out of Stock', cls: 'bg-red-50 text-red-700 border-red-100' };
    if (n <= 5)  return { label: 'Low',         cls: 'bg-yellow-50 text-yellow-700 border-yellow-100' };
    return { label: 'OK',                        cls: 'bg-green-50 text-green-700 border-green-100' };
  };

  const headerBtn = (label, col) => (
    <button
      onClick={() => toggleSort(col)}
      className={`inline-flex items-center gap-1 ${sort === col ? 'text-orange-700' : 'text-gray-600'} hover:text-orange-700`}
      title={`Sort by ${label}`}
    >
      {label}
      <ArrowUpDown className="w-4 h-4" />
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>

          <div className="flex items-center gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Home className="w-4 h-4" /><span className="hidden sm:inline">Dashboard</span>
            </Link>
            <Link to="/admin/products/new" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700">
              <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Product</span>
            </Link>
            <form onSubmit={applySearch} className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, description, brand, category, SKU…"
                className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent w-72"
              />
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-3 px-4">{headerBtn('Name', 'name')}</th>
                  <th className="py-3 px-4">Description</th>
                  <th className="py-3 px-4">Variant</th>
                  <th className="py-3 px-4">{headerBtn('Price', 'price')}</th>
                  <th className="py-3 px-4">{headerBtn('Stock', 'stock')}</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Brand</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">SKU</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="py-6 px-4 text-gray-500" colSpan={10}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td className="py-6 px-4 text-gray-500" colSpan={10}>No products found.</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={`${r.product_id}-${r.variant_id}`} className="border-t last:border-b-0">
                      <td className="py-3 px-4 font-medium text-gray-900">{r.product_name}</td>
                      <td className="py-3 px-4 text-gray-600 max-w-[280px]">{clip(r.description, 90)}</td>
                      <td className="py-3 px-4">{r.pack_size}</td>
                      <td className="py-3 px-4">{fmt(r.price, r.currency || currency)}</td>
                      <td className="py-3 px-4">{r.stock_qty}</td>
                      <td className="py-3 px-4">
                        {(() => {
                          const b = stockBadge(r.stock_qty);
                          return (
                            <span className={`px-2 py-1 rounded-full text-xs border ${b.cls}`}>
                              {b.label}
                            </span>
                          );
                        })()}
                        <div className="text-[11px] text-gray-500 mt-0.5">state: {r.status}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{r.brand || '—'}</td>
                      <td className="py-3 px-4 text-gray-600">{r.category || '—'}</td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-500">{r.sku}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/products/${r.product_id}/edit?v=${r.variant_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" /> Edit
                          </Link>

                          <button
                            onClick={() => setConfirmItem({ id: r.product_id, name: r.product_name })}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmItem ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-semibold">Delete product</h3>
              <button className="p-1 text-gray-500 hover:text-gray-700" onClick={() => setConfirmItem(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p>
                Are you sure you want to permanently delete{' '}
                <span className="font-semibold">{confirmItem.name}</span>?
                This will remove the product and its variants.
              </p>
              {error && <div className="text-sm text-red-600">{error}</div>}
            </div>
            <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmItem(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
